import {
  createFileRoute,
  getRouteApi,
  useRouter,
} from "@tanstack/react-router";
import { LogOut } from "lucide-react";
import { useState } from "react";
import { toast } from "react-hot-toast";

import { Link } from "@/components/link";
import {
  SettingsRow,
  SettingsSection,
} from "@/components/settings/settings-section";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiErrorMessage } from "@/lib/api";
import { forgetSession } from "@/lib/session";
import { updateDisplayName } from "@/lib/settings-api";

const settingsRoute = getRouteApi("/admin/settings");

const AccountPage = () => {
  const { session } = settingsRoute.useRouteContext();
  const router = useRouter();
  const savedName = session.user?.name ?? "";
  const [name, setName] = useState(savedName);
  const [saving, setSaving] = useState(false);

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && trimmed !== savedName && !saving;

  const saveName = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) {
      return;
    }
    setSaving(true);
    const saved = await toast
      .promise(updateDisplayName(trimmed), {
        error: apiErrorMessage,
        loading: "Saving name...",
        success: "Name updated",
      })
      .then(
        () => true,
        () => false
      );
    if (saved) {
      forgetSession();
      await router.invalidate();
    }
    setSaving(false);
  };

  return (
    <div className="flex flex-col gap-10">
      <SettingsSection title="Profile">
        <form onSubmit={saveName}>
          <SettingsRow htmlFor="display-name" label="Name">
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                autoComplete="name"
                id="display-name"
                maxLength={120}
                onChange={(event) => setName(event.target.value)}
                value={name}
              />
              <Button disabled={!canSave} type="submit">
                Save
              </Button>
            </div>
          </SettingsRow>
        </form>
        <SettingsRow
          description="You sign in with a one-time code sent here."
          htmlFor="account-email"
          label="Email"
        >
          <Input
            disabled
            id="account-email"
            readOnly
            value={session.user?.email ?? ""}
          />
        </SettingsRow>
        <SettingsRow label="Group">
          <div>
            <Badge variant="secondary">
              {session.staff?.groupName ?? "No group"}
            </Badge>
          </div>
        </SettingsRow>
      </SettingsSection>

      <SettingsSection title="Session">
        <SettingsRow label="Sign out">
          <div>
            <Link
              className={buttonVariants({ variant: "outline" })}
              href="/logout"
            >
              <LogOut aria-hidden data-icon="inline-start" />
              Sign out
            </Link>
          </div>
        </SettingsRow>
      </SettingsSection>
    </div>
  );
};

export const Route = createFileRoute("/admin/settings/")({
  component: AccountPage,
});
